/**
 * Bedrock prompt + parser for refining a raw voice-note transcript into the
 * structured AHP progress-note draft (`ProgressNoteDraftFields`).
 *
 * Mirrors the `shared/calls/summarize-prompt.ts` pattern: a pure prompt
 * builder, a defensive JSON parser (models wrap JSON in prose/fences), and a
 * deterministic plain-text formatter. The five AHP fields are the canonical
 * structure — providers paste the plain-text block into AHP — with the
 * clinical Presentation / Interventions / Response / Plan framing mapped onto
 * them in the section headings.
 *
 * Honesty rule (same as the call pipeline): the model must never invent
 * content. A thin transcript yields a thin draft plus `qualityWarnings`, never
 * fabricated detail.
 */

import {
  emptyProgressNoteDraft,
  PROGRESS_NOTE_FIELD_KEYS,
  PROGRESS_NOTE_MIN_WORDS,
  validateProgressNoteDraft,
  type ProgressNoteDraftFields,
} from './progress-note'

export type RefineNotePromptInput = {
  /** Raw Transcribe text of the provider's voice note. */
  transcript: string
  /** Member (client) first name for natural references, e.g. "Sam". */
  memberFirstName: string
  /** Human-readable session date/time, e.g. "Tuesday 29 September 2026, 2:00 pm". */
  sessionDateTime: string
  /** Service type label from the session, e.g. "Support Work". */
  serviceType?: string
}

export function buildRefineNotePrompt(input: RefineNotePromptInput): string {
  const member = input.memberFirstName.trim() || 'the member'
  const service = input.serviceType?.trim()

  return `You are a clinical documentation assistant for an NDIS support organisation in Australia.
A provider (therapist / support worker) has dictated a voice note after a session. Refine their
rambling spoken transcript into a structured progress-note draft they will review and paste into
the AHP clinical system.

Session context:
- Member first name: ${member}
- Session date/time: ${input.sessionDateTime}${service ? `\n- Service type: ${service}` : ''}

Rules:
- Write in clear, professional Australian English, third person ("${member} engaged well…").
- Use ONLY information present in the transcript. Do NOT invent places, activities, quotes,
  observations or outcomes. If the transcript says nothing about a field, return "" for it.
- Remove filler words, repetitions and self-corrections; keep every clinical fact.
- Frame the session summary clinically: presentation (how ${member} presented), interventions
  (what the provider did), and response (how ${member} responded) — in that order, as prose.
- Frame SMART goals comments as the plan: next steps and follow-ups actually mentioned.
- Aim for at least ${PROGRESS_NOTE_MIN_WORDS} words in sessionSummary ONLY when the transcript
  contains enough material; never pad with fabricated detail.
- No medical diagnoses or legal conclusions. Preserve any safety or safeguarding concerns verbatim.

Return ONLY valid JSON with exactly this shape (all values strings, no markdown):
{
  "visitedPlaces": "Places/locations visited or where the session happened",
  "sessionPrep": "Preparation and research done before the session",
  "sessionSummary": "What happened: presentation, interventions and response",
  "goalProgress": "Progress toward the member's goals observed this session",
  "smartGoalsComments": "SMART goal comments and plan / next steps"
}

Transcript:
${input.transcript}`
}

/**
 * Outcome of parsing the model's response.
 *
 * - `ok`: valid JSON object with at least one non-empty field.
 * - `empty`: valid JSON object but EVERY field is empty. This is a legitimate
 *   answer — the prompt tells the model to return "" for anything the
 *   transcript doesn't cover, so a test/non-clinical dictation ("testing,
 *   testing…") correctly yields all-empty. Callers should fall back to the
 *   verbatim transcript, not fail.
 * - `unparseable`: no usable JSON object at all. `reason` is a content-free
 *   code that is safe to log.
 */
export type RefinedNoteParseResult =
  | { status: 'ok'; fields: ProgressNoteDraftFields; recognisedKeys: number }
  | { status: 'empty'; fields: ProgressNoteDraftFields; recognisedKeys: number }
  | { status: 'unparseable'; reason: RefinedNoteParseFailureReason }

export type RefinedNoteParseFailureReason =
  | 'blank-output'
  | 'no-json-object'
  | 'invalid-json'

/**
 * Index just past the end of the balanced `{…}` object starting at `start`
 * (string- and escape-aware), or -1 when it never closes.
 */
function findObjectEnd(text: string, start: number): number {
  let depth = 0
  let inString = false
  let escaped = false
  for (let i = start; i < text.length; i++) {
    const ch = text[i]
    if (inString) {
      if (escaped) escaped = false
      else if (ch === '\\') escaped = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') inString = true
    else if (ch === '{') depth++
    else if (ch === '}') {
      depth--
      if (depth === 0) return i + 1
    }
  }
  return -1
}

/** Every parseable top-level JSON object in `text`, in order of appearance. */
function collectJsonObjects(text: string): { objects: Record<string, unknown>[]; sawBrace: boolean } {
  const objects: Record<string, unknown>[] = []
  let sawBrace = false
  let from = text.indexOf('{')
  while (from >= 0) {
    sawBrace = true
    const end = findObjectEnd(text, from)
    let next = from + 1
    if (end > from) {
      try {
        const value: unknown = JSON.parse(text.slice(from, end))
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          objects.push(value as Record<string, unknown>)
          next = end // top-level only: skip past this object's nested braces
        }
      } catch {
        // Prose like "{see below}" before the real object — try the next brace.
      }
    }
    from = text.indexOf('{', next)
  }
  return { objects, sawBrace }
}

const normaliseKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '')

const EXPECTED_KEYS = new Set<string>(PROGRESS_NOTE_FIELD_KEYS.map(normaliseKey))

function recognisedKeyCount(obj: Record<string, unknown>): number {
  const seen = new Set<string>()
  for (const key of Object.keys(obj)) {
    const norm = normaliseKey(key)
    if (EXPECTED_KEYS.has(norm)) seen.add(norm)
  }
  return seen.size
}

/** Most recognised keys wins; ties go to the LAST object. Ignores zero-key objects. */
function pickBest(objects: Record<string, unknown>[]): Record<string, unknown> | null {
  let best: Record<string, unknown> | null = null
  let bestCount = 0
  for (const obj of objects) {
    const count = recognisedKeyCount(obj)
    if (count > 0 && count >= bestCount) {
      best = obj
      bestCount = count
    }
  }
  return best
}

/**
 * Choose the model's real answer from free-form output. The model may echo or
 * quote the transcript (which could itself contain JSON-looking text), so the
 * first object is NOT trusted blindly:
 *
 * 1. Objects with zero recognised keys are ignored.
 * 2. A markdown-fenced block wins over unfenced text.
 * 3. Otherwise the object with the most recognised keys; ties → the last one.
 *
 * When parseable objects exist but none carries a recognised key, returns an
 * empty object (valid-but-empty → transcript fallback); with none at all,
 * returns the failure reason.
 */
function extractNoteObject(
  raw: string,
): { value: Record<string, unknown> } | { reason: 'no-json-object' | 'invalid-json' } {
  const fencedBodies: string[] = []
  const unfenced = raw
    .replace(/```[a-z]*[ \t]*\r?\n?([\s\S]*?)```/gi, (_m, body: string) => {
      fencedBodies.push(body)
      return ' '
    })
    // Unterminated fence (e.g. truncated output): drop just the opening marker.
    .replace(/```[a-z]*/gi, ' ')

  const fenced = collectJsonObjects(fencedBodies.join('\n'))
  const loose = collectJsonObjects(unfenced)
  const best = pickBest(fenced.objects) ?? pickBest(loose.objects)
  if (best) return { value: best }

  const any = fenced.objects.length + loose.objects.length > 0
  if (any) return { value: {} }
  return { reason: fenced.sawBrace || loose.sawBrace ? 'invalid-json' : 'no-json-object' }
}

function coerceFieldValue(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean)
      .join('\n')
  }
  return ''
}

/**
 * Tolerant parse of the model's response into the five draft fields. Strips
 * code fences / leading prose, picks the real note object (see
 * `extractNoteObject`), accepts missing fields (→ ""), matches keys
 * case/underscore-insensitively and flattens string arrays. Distinguishes
 * "valid but all empty" from "unparseable".
 */
export function parseRefinedNoteOutput(raw: string): RefinedNoteParseResult {
  if (!raw || !raw.trim()) return { status: 'unparseable', reason: 'blank-output' }
  const extracted = extractNoteObject(raw)
  if ('reason' in extracted) return { status: 'unparseable', reason: extracted.reason }
  const byNormalisedKey = new Map<string, unknown>()
  for (const [key, value] of Object.entries(extracted.value)) {
    const norm = normaliseKey(key)
    if (!byNormalisedKey.has(norm)) byNormalisedKey.set(norm, value)
  }
  const fields = {} as ProgressNoteDraftFields
  let recognisedKeys = 0
  for (const key of PROGRESS_NOTE_FIELD_KEYS) {
    const norm = normaliseKey(key)
    if (byNormalisedKey.has(norm)) recognisedKeys++
    fields[key] = coerceFieldValue(byNormalisedKey.get(norm))
  }
  const hasContent = PROGRESS_NOTE_FIELD_KEYS.some((key) => fields[key].length > 0)
  return hasContent
    ? { status: 'ok', fields, recognisedKeys }
    : { status: 'empty', fields, recognisedKeys }
}

/**
 * Back-compat wrapper: the fields when the output is usable, null when it is
 * unparseable OR all-empty. New code should use `parseRefinedNoteOutput` so
 * the two cases can be handled differently.
 */
export function parseRefinedNoteJson(raw: string): ProgressNoteDraftFields | null {
  const result = parseRefinedNoteOutput(raw)
  return result.status === 'ok' ? result.fields : null
}

/**
 * Content-free description of a model response, safe for logs. NEVER include
 * any of the text (it is derived from a clinical dictation).
 */
export type RefineOutputShape = {
  outputLength: number
  stopReason: string
  modelId: string
  /** Number of the five fields that came back non-empty. */
  fieldCount: number
  /** Number of the five expected keys present at all. */
  recognisedKeys: number
  parse: RefinedNoteParseResult['status']
  reason?: RefinedNoteParseFailureReason
}

export function describeRefineOutputShape(
  raw: string,
  meta: { stopReason?: string; modelId?: string } = {},
): RefineOutputShape {
  const result = parseRefinedNoteOutput(raw)
  const base = {
    outputLength: raw.length,
    stopReason: meta.stopReason ?? 'unknown',
    modelId: meta.modelId ?? 'unknown',
  }
  if (result.status === 'unparseable') {
    return { ...base, fieldCount: 0, recognisedKeys: 0, parse: 'unparseable', reason: result.reason }
  }
  const fieldCount = PROGRESS_NOTE_FIELD_KEYS.filter((k) => result.fields[k].length > 0).length
  return {
    ...base,
    fieldCount,
    recognisedKeys: result.recognisedKeys,
    parse: result.status,
  }
}

/** Provider-visible flag on a draft that is just the transcript, unstructured. */
export const AI_COULD_NOT_STRUCTURE_WARNING =
  "AI couldn't structure this note — transcript shown as-is."

/**
 * Verbatim fallback draft: the transcript as the session summary, every other
 * field empty — nothing invented. Used when the model returns nothing usable.
 */
export function verbatimTranscriptDraft(transcript: string): ProgressNoteDraftFields {
  return { ...emptyProgressNoteDraft(), sessionSummary: transcript.trim() }
}

/**
 * Section headings for the copy-paste block. The five AHP fields are canonical;
 * the Presentation / Interventions / Response / Plan clinical framing is mapped
 * onto them in the headings so the structure reads clinically without breaking
 * the AHP field shape providers paste into.
 */
export const REFINED_NOTE_SECTIONS: readonly {
  field: keyof ProgressNoteDraftFields
  heading: string
}[] = [
  { field: 'visitedPlaces', heading: 'VISITED PLACES' },
  { field: 'sessionPrep', heading: 'SESSION PREPARATION & RESEARCH' },
  { field: 'sessionSummary', heading: 'SESSION SUMMARY (PRESENTATION, INTERVENTIONS & RESPONSE)' },
  { field: 'goalProgress', heading: 'GOAL PROGRESS (RESPONSE & OUTCOMES)' },
  { field: 'smartGoalsComments', heading: 'SMART GOALS COMMENTS (PLAN)' },
]

/**
 * Deterministic single copy-paste block built from the parsed fields — never
 * model-generated, so the structure is always intact. Empty fields render as
 * an em dash so the provider sees the gap rather than a silently missing section.
 */
export function formatRefinedNotePlainText(fields: ProgressNoteDraftFields): string {
  return REFINED_NOTE_SECTIONS.map(
    ({ field, heading }) => `${heading}\n${fields[field].trim() || '—'}`,
  ).join('\n\n')
}

/**
 * Non-blocking quality warnings for a refined draft, derived from the shared
 * AHP quality gate. A thin transcript still produces a best-effort draft; these
 * messages tell the provider what AHP will consider missing.
 */
export function refinedNoteQualityWarnings(fields: ProgressNoteDraftFields): string[] {
  const result = validateProgressNoteDraft(fields)
  return result.issues.map((issue) => issue.message)
}

/** Minimum transcript length (words) below which refinement is pointless noise. */
export const MIN_REFINABLE_TRANSCRIPT_WORDS = 5

export function isTranscriptRefinable(transcript: string): boolean {
  return (
    transcript.trim().split(/\s+/).filter(Boolean).length >= MIN_REFINABLE_TRANSCRIPT_WORDS
  )
}
